import { useState, useEffect } from "react";
import { signInWithCustomToken } from "firebase/auth";
import { auth } from "../firebase";
import { useNavigate } from "react-router-dom";
import TurnstileWidget from "../components/TurnstileWidget";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showLogin, setShowLogin] = useState(false);
  const [emailValid, setEmailValid] = useState(null);
  const [passwordValid, setPasswordValid] = useState(null);
  const [loginError, setLoginError] = useState("");
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetEmailValid, setResetEmailValid] = useState(false);
  const [resetStatus, setResetStatus] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileKey, setTurnstileKey] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setShowLogin(true), 3500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setEmailValid(emailRegex.test(email));
  }, [email]);

  useEffect(() => {
    setPasswordValid(password.length >= 6);
  }, [password]);

  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setResetEmailValid(emailRegex.test(resetEmail));
  }, [resetEmail]);

  useEffect(() => {
    if (loginError) {
      setShowErrorToast(true);
      const timer = setTimeout(() => {
        setLoginError("");
        setShowErrorToast(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [loginError]);

const loginUser = async (e) => {
  e.preventDefault();
  setLoginError("");
  if (!emailValid || !passwordValid) {
    setLoginError("Por favor, ingresa un email válido y una contraseña de al menos 6 caracteres.");
    return;
  }
  try {
    // Las credenciales van al BACKEND, que verifica con Firebase Admin (server-to-server).
    // El browser nunca recibe ni expone el passwordHash.
    const res = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/auth/login`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, turnstileToken }),
      }
    );
    const data = await res.json();

    if (!res.ok) {
      // Siempre refrescar el widget tras un intento fallido
      setTurnstileToken("");
      setTurnstileKey((k) => k + 1);

      if (res.status === 429) {
        setLoginError("Demasiados intentos. Espera unos minutos e inténtalo de nuevo.");
      } else if (data.error === "auth/user-disabled") {
        setLoginError("Esta cuenta está deshabilitada. Contacta soporte.");
      } else if (data.error === "captcha-required" || data.error === "captcha-failed") {
        setLoginError("Verificación de seguridad fallida. Intenta de nuevo.");
      } else {
        // Mensaje genérico: no revelar si el email existe o no
        setLoginError("Correo o contraseña incorrecta. Inténtalo de nuevo.");
      }
      return;
    }

    // El backend devuelve un customToken — el browser firma con este token.
    // signInWithCustomToken no expone el hash de contraseña en el network.
    await signInWithCustomToken(auth, data.customToken);
    navigate("/");
  } catch (error) {
    setLoginError("Error de conexión. Verifica tu internet e inténtalo de nuevo.");
  }
};

  const handlePasswordReset = async () => {
    if (!resetEmailValid || resetLoading || resetSuccess) return;
    setResetStatus("");
    setResetLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/email/send-reset-password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: resetEmail }),
        }
      );
      const data = await res.json();
      if (res.status === 404 && data.error === "user-not-found") {
        setResetStatus("No existe ninguna cuenta registrada con ese correo en SIT.");
        return;
      }
      if (!res.ok) throw new Error(data.error || "error");
      setResetSuccess(true);
      setResetStatus(`Correo enviado a ${resetEmail}. Revisa también tu carpeta de spam.`);
    } catch (error) {
      if (error.message.includes("15 minutos")) {
        setResetStatus("Demasiados intentos. Espera 15 minutos e inténtalo de nuevo.");
      } else {
        setResetStatus("Ocurrió un error al enviar el correo. Inténtalo de nuevo.");
      }
    } finally {
      setResetLoading(false);
    }
  };

  const closeModal = () => {
    setShowResetModal(false);
    setResetEmail("");
    setResetStatus("");
    setResetLoading(false);
    setResetSuccess(false);
  };
  
  return (
    <div className="relative min-h-screen flex items-center justify-center bg-gradient-to-tr from-[#0f2027] via-[#203a43] to-[#2c5364] overflow-hidden text-white select-none">
      <ParticlesBG />

      {!showLogin && (
        <div className="text-center px-6 z-10">
          <h1
            className="text-6xl md:text-8xl font-extrabold tracking-wide flex justify-center gap-4 drop-shadow-2xl"
            style={{ animation: "fadeSlideIn 2s ease forwards" }}
          >
            <span className="text-yellow-400">SIT</span>
          </h1>
          <p
            className="mt-4 text-xl md:text-2xl font-light italic drop-shadow-lg"
            style={{ animation: "fadeSlideIn 2s ease forwards", animationDelay: "1.5s" }}
          >
            Social Interactive Technology
          </p>
        </div>
      )}

      {showLogin && (
        <div className="relative z-10 flex flex-col items-center">
          <div className="bg-white bg-opacity-10 backdrop-blur-md rounded-3xl shadow-2xl p-10 max-w-md w-full mx-4 animate-fadeSlideIn border border-white border-opacity-20">
            <h2
              className="text-center text-5xl md:text-6xl font-extrabold mb-8 flex justify-center gap-3 drop-shadow-xl"
              style={{ animation: "subtleMove 4s ease-in-out infinite" }}
            >
              <span className="text-yellow-400">SIT</span>
            </h2>
            <p className="text-center text-green-200 mb-8 italic drop-shadow-md">
              Social Interactive Technology
            </p>

            <form onSubmit={loginUser} className="space-y-6">
              <div className="relative">
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={`w-full pl-12 pr-4 py-3 rounded-xl bg-white bg-opacity-20 placeholder-white placeholder-opacity-75 text-white focus:outline-none focus:ring-2 ${
                    emailValid === false ? "ring-red-500" : "ring-yellow-400"
                  } ring-opacity-75 transition`}
                />
                <svg
                  className="w-6 h-6 absolute left-4 top-1/2 transform -translate-y-1/2 text-yellow-400 pointer-events-none"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 5h18M3 7l9 6 9-6M3 19h18a2 2 0 002-2V7a2 2 0 00-2-2H3a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>

              <div>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Contraseña"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className={`w-full pl-12 pr-12 py-3 rounded-xl bg-white bg-opacity-20 placeholder-white placeholder-opacity-75 text-white focus:outline-none focus:ring-2 ${
                      passwordValid === false || loginError ? "ring-red-500" : "ring-green-400"
                    } ring-opacity-75 transition`}
                  />
                  {/* Ícono candado izquierda */}
                  <svg
                    className="w-6 h-6 absolute left-3.5 top-1/2 -translate-y-1/2 text-green-400 pointer-events-none"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 11c1.104 0 2 .896 2 2v1a2 2 0 11-4 0v-1c0-1.104.896-2 2-2z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 11V7a5 5 0 1110 0v4" />
                    <rect x="5" y="11" width="14" height="10" rx="2" ry="2" />
                  </svg>
                  {/* Botón mostrar/ocultar contraseña */}
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition"
                    tabIndex={-1}
                    aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                  </button>
                </div>
                <p
                  onClick={() => setShowResetModal(true)}
                  className="mt-2 text-yellow-300 text-sm font-semibold cursor-pointer hover:underline"
                >
                  He olvidado mi contraseña
                </p>
              </div>

              {/* Turnstile — invisible en dev si no hay SITE_KEY */}
              <TurnstileWidget
                key={turnstileKey}
                onVerify={(token) => setTurnstileToken(token)}
                onExpire={() => setTurnstileToken("")}
                theme="dark"
              />

              <button
                type="submit"
                disabled={!emailValid || !passwordValid || !turnstileToken}
                className="w-full bg-gradient-to-r from-yellow-400 via-green-400 to-teal-500 text-white font-semibold py-3 rounded-xl shadow-lg transition hover:from-yellow-500 hover:via-green-500 hover:to-teal-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Entrar
              </button>
            </form>

            <p
              onClick={() => navigate("/register")}
              className="mt-6 text-center text-green-300 cursor-pointer hover:underline"
            >
              ¿No tienes cuenta? Regístrate
            </p>
          </div>

          {/* Botón para descargar la app */}
          <button
            onClick={() => navigate("/download")}
            className="mt-6 flex items-center gap-2 bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-600 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:scale-105 hover:from-blue-600 hover:to-indigo-700 transition-all duration-300 animate-pulse-glow"
          >
            <span className="text-xl">📲</span>
            Descarga nuestra App
          </button>
        </div>
      )}

      {/* TOAST ERROR CONTRASEÑA */}
      {showErrorToast && (
        <div className="fixed top-6 left-1/2 transform -translate-x-1/2 bg-red-500 text-white py-3 px-6 rounded-xl shadow-xl z-50 animate-slideDown">
          <p className="font-semibold">{loginError}</p>
        </div>
      )}

      {/* MODAL RESET PASSWORD */}
      {showResetModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeInScale"
          role="dialog"
          aria-modal="true"
          aria-labelledby="reset-password-title"
        >
          <div className="bg-white rounded-3xl p-10 max-w-md w-full relative shadow-2xl ring-1 ring-black ring-opacity-5">
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-900 text-3xl font-extrabold transition-colors"
              aria-label="Cerrar modal"
            >
              &times;
            </button>
            <h3
              id="reset-password-title"
              className="text-3xl font-extrabold mb-6 text-center text-green-600 drop-shadow-md"
            >
              Recuperar contraseña
            </h3>
            <p className="mb-8 text-center text-gray-700 font-medium tracking-wide">
              Ingrese su email para enviarle el enlace de recuperación.
            </p>
            <input
              type="email"
              placeholder="Email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              className={`w-full px-5 py-4 rounded-xl border shadow-sm text-gray-900 font-semibold tracking-wide placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-green-400 focus:border-green-500 transition ${
                resetEmailValid
                  ? "border-green-400 focus:ring-green-500"
                  : "border-red-400 focus:ring-red-500"
              }`}
              autoComplete="email"
              spellCheck={false}
            />
            <p className="mt-2 min-h-[1.25rem] text-center text-sm font-semibold text-red-600 select-text">
              {!resetEmailValid && resetEmail && "Email no válido"}
            </p>
            {resetStatus && (
              <p
                className={`mt-6 text-center text-sm font-semibold select-text ${
                  resetSuccess ? "text-green-700" : "text-red-700"
                }`}
              >
                {resetStatus}
              </p>
            )}
            <button
              onClick={handlePasswordReset}
              disabled={!resetEmailValid || resetLoading || resetSuccess}
              className="mt-8 w-full bg-gradient-to-r from-green-500 to-teal-600 text-white font-bold py-4 rounded-xl shadow-lg hover:from-green-600 hover:to-teal-700 active:scale-95 active:shadow-inner transition-transform disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              type="button"
            >
              {resetLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Enviando...
                </>
              ) : resetSuccess ? (
                "✓ Correo enviado"
              ) : (
                "Enviar correo"
              )}
            </button>
          </div>

          <style>{`
            @keyframes fadeInScale {
              0% {
                opacity: 0;
                transform: scale(0.9);
              }
              100% {
                opacity: 1;
                transform: scale(1);
              }
            }
            .animate-fadeInScale {
              animation: fadeInScale 0.3s ease forwards;
            }
          `}</style>
        </div>
      )}


      {/* ── Botón de aviso legal (esquina inferior derecha) ── */}
      <button
        onClick={() => setShowLegalModal(true)}
        title="Aviso legal importante"
        className="fixed bottom-5 right-5 z-50 w-10 h-10 flex items-center justify-center rounded-full bg-yellow-400 text-black font-extrabold text-lg shadow-lg hover:bg-yellow-300 hover:scale-110 transition-all duration-200"
      >
        !
      </button>

      {/* ── Modal de aviso legal ── */}
      {showLegalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-auto overflow-hidden">
            <div className="bg-yellow-400 px-6 py-4 flex items-center gap-3">
              <span className="text-2xl">⚠️</span>
              <h2 className="text-lg font-extrabold text-black">Aviso Importante</h2>
            </div>
            <div className="px-6 py-5 space-y-3 text-sm text-gray-700 max-h-[60vh] overflow-y-auto">
              <p>
                <strong>SIT (Social Interactive Technology)</strong> es una plataforma digital independiente en fase beta, desarrollada con fines de prueba, validación y mejora continua.
              </p>
              <p>
                Esta plataforma <strong>no está afiliada, asociada ni respaldada oficialmente</strong> por ninguna institución educativa, incluyendo universidades donde puedan encontrarse usuarios registrados.
              </p>
              <p>
                El uso de SIT es completamente <strong>voluntario</strong>. Cada usuario es responsable del contenido que publica, comparte o comunica dentro de la plataforma.
              </p>
              <p>
                SIT cuenta con sistemas de <strong>moderación, monitoreo y reporte</strong> con el objetivo de mantener un entorno seguro y respetuoso. No obstante, el uso indebido de la plataforma puede conllevar la suspensión o eliminación de cuentas.
              </p>
              <p>
                Al registrarse y utilizar SIT, el usuario acepta estos términos y se compromete a hacer un <strong>uso responsable</strong> de la plataforma.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-100">
              <button
                onClick={() => setShowLegalModal(false)}
                className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-semibold py-2.5 rounded-xl transition"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Animaciones personalizadas */}
      <style>{`
        @keyframes fadeSlideIn {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes subtleMove {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(5px); }
        }
        @keyframes slideDown {
          0% { transform: translateY(-100px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
        .animate-fadeSlideIn { animation: fadeSlideIn 1s ease forwards; }
        .animate-slideDown { animation: slideDown 0.5s ease-out forwards; }
        @keyframes bounceInUp {
        0% {
          transform: translateY(100%) scale(0.9);
          opacity: 0;
        }
        60% {
          transform: translateY(-20%) scale(1.05);
          opacity: 1;
        }
        80% {
          transform: translateY(10%) scale(0.95);
        }
        100% {
          transform: translateY(0) scale(1);
        }
      }
      .animate-bounce-in {
        animation: bounceInUp 0.8s ease-out forwards;
      }
        @keyframes pulseGlow {
        0%, 100% {
          box-shadow: 0 0 0px rgba(255, 255, 255, 0.3);
          transform: scale(1);
        }
        50% {
          box-shadow: 0 0 20px rgba(255, 255, 255, 0.5);
          transform: scale(1.05);
        }
      }
      .animate-pulse-glow {
        animation: pulseGlow 2.5s ease-in-out infinite;
      }
      `}</style>

    </div>
  );
}

function ParticlesBG() {
  return (
    <svg
      className="absolute inset-0 w-full h-full"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
    >
      <circle className="particle" cx="10%" cy="20%" r="2" fill="#F59E0B" opacity="0.7">
        <animate attributeName="cy" values="20%;80%;20%" dur="10s" repeatCount="indefinite" begin="0s" />
        <animate attributeName="cx" values="10%;15%;10%" dur="6s" repeatCount="indefinite" begin="0s" />
      </circle>
      <circle className="particle" cx="30%" cy="40%" r="3" fill="#46bb84" opacity="0.6">
        <animate attributeName="cy" values="40%;10%;40%" dur="15s" repeatCount="indefinite" begin="0s" />
        <animate attributeName="cx" values="30%;25%;30%" dur="10s" repeatCount="indefinite" begin="0s" />
      </circle>
      <circle className="particle" cx="70%" cy="70%" r="2.5" fill="#22c55e" opacity="0.5">
        <animate attributeName="cy" values="70%;40%;70%" dur="12s" repeatCount="indefinite" begin="0s" />
        <animate attributeName="cx" values="70%;75%;70%" dur="8s" repeatCount="indefinite" begin="0s" />
      </circle>
    </svg>
  );
}
